<script setup lang="ts">
const isGoogleInspect = useState('isGoogleInspect', () => false)

const { navigationElements } = useNavigation();
const { isLoggedIn, logout, user } = useUser();
const { trackViewCart } = useGtmEvents()
const { count } = useCart();
const { t } = useI18n();
const route = useRoute();

const navigation = ref(null);
const { height } = useElementSize(navigation);
const accountMenu = ref();
const searchBar = ref(false);
const searchBarDrawer = ref(null);
const mobileMenu = ref(false);
const mobileMenuDrawer = ref(null);

const accountMenuItems = ref([
  {
    label: t("account.yourProfile"),
    command: () => navigateTo("/account"),
  },
  {
    separator: true,
  },
  {
    label: t("account.orderHistory"),
    command: () => navigateTo("/account/order"),
  },
  {
    separator: true,
  },
  {
    label: t("account.wishlist"),
    command: () => navigateTo("/account/wishlist"),
  },
  {
    separator: true,
  },
  {
    label: t("account.address"),
    command: () => navigateTo("/account/address"),
  },
  {
    separator: true,
  },
  {
    label: t("form.logout"),
    icon: "pi pi-sign-out",
    command: async () => {
      await logout();
    },
  },
]);

async function toggle(event: MouseEvent) {
  if (!isLoggedIn.value) {
    await navigateTo({ query: { ...route.query, modal: "login" } });
  } else {
    accountMenu.value.toggle(event);
  }
}

function openDrawer() {
  if (!mobileMenuDrawer.value?.containerVisible) {
    mobileMenu.value = true
  }
}

function openSearchBar() {
  if (!searchBarDrawer.value?.containerVisible) {
    searchBar.value = true
  }
}

watch(
  () => route.path,
  () => {
    mobileMenu.value = false;
    searchBar.value = false;
  }
);
</script>

<template>
  <div 
    ref="navigation" 
    class="bg-white dark:bg-surface-950 shadow relative"
  >
    <div class="border-b border-primary">
      <nav
        class="container grid grid-cols-[1fr_2fr] lg:grid-cols-3 lg:gap-8 w-full items-center py-6"
      >
        <div class="flex items-center">
          <NuxtLink
            to="/"
            class="flex h-11 lg:h-14 hover:opacity-80 transition-opacity"
          >
            <img
              src="~/assets/logo.png"
              alt="Kavos Bankas"
              class="w-full max-w-40 h-full object-contain"
            />
          </NuxtLink>
        </div>

        <ClientOnly>
          <SearchBar v-if="!isGoogleInspect" class="max-lg:hidden w-full" />
        </ClientOnly>

        <div v-if="!isGoogleInspect" class="flex items-center justify-end gap-2 lg:gap-4">
          <!-- mobile start -->
          <Button
            :icon="`pi ${mobileMenu ? 'pi-times' : 'pi-bars'}`"
            text
            class="lg:hidden block"
            severity="secondary"
            :aria-label="`${mobileMenu ? 'Close' : 'Menu'}`"
            @click="openDrawer"
          />

          <Button
            v-if="!isGoogleInspect"
            icon="pi pi-search"
            aria-label="Search"
            text
            severity="secondary"
            class="lg:hidden block"
            @click="openSearchBar"
          />
          <!-- mobile end -->

          <ClientOnly>
            <Avatar
              v-if="!isGoogleInspect"
              :label="isLoggedIn ? user?.firstName.charAt(0) : undefined"
              :icon="isLoggedIn ? undefined : 'pi pi-user'"
              shape="circle"
              aria-haspopup="true"
              aria-controls="overlay_menu"
              class="cursor-pointer hover:opacity-80 transition-opacity bg-[#d0dbb7] dark:bg-primary"
              @click="toggle"
            />
          </ClientOnly>

          <Menu
            v-if="!isGoogleInspect"
            ref="accountMenu"
            id="overlay_menu"
            :model="accountMenuItems"
            :popup="true"
          />

          <NuxtLink
            v-if="!isGoogleInspect"
            :to="{ query: { ...$route.query, sidebar: 'cart' } }"
            class="p-1 hover:opacity-80 transition-opacity"
            @click="trackViewCart()"
          >
            <OverlayBadge :value="count" size="small" severity="">
              <i class="pi pi-shopping-bag text-lg mr-2" />
            </OverlayBadge>
          </NuxtLink>
        </div>
      </nav>
    </div>

    <nav class="max-lg:hidden container">
      <ul class="flex flex-nowrap gap-4">
        <NavigationVariantFlatElementDesktop
          v-for="navigationElement in navigationElements"
          :key="navigationElement.id"
          :navigation-element="navigationElement"
        />
      </ul>
    </nav>

    <Drawer
      key="firstNavigationLevel"
      ref="mobileMenuDrawer"
      v-model:visible="mobileMenu"
      position="left"
      block-scroll
      :show-close-icon="false"
      :modal="false"
      :style="{
        top: `${height}px`,
        paddingBottom: `${height}px`,
        position: 'relative',
      }"
      :pt="{
        header: 'p-0',
        content: 'divide-y h-full flex flex-col overflow-y-auto p-0',
      }"
    >
      <NavigationElement
        v-for="navigationElement in navigationElements"
        :key="navigationElement.id"
        :navigation-element="navigationElement"
        :height="height"
      />
    </Drawer>

    <Drawer
      v-if="!isGoogleInspect"
      v-model:visible="searchBar"
      ref="searchBarDrawer"
      position="left"
      block-scroll
      :show-close-icon="false"
      :modal="false"
      :style="{
        top: `${height}px`,
        width: '100%',
        paddingBottom: `${height}px`,
      }"
      :pt="{ header: 'p-0' }"
      class="lg:bg-white/20 lg:backdrop-blur-md max-lg:pt-6"
    >
      <SearchBar class="w-full" />
    </Drawer>
  </div>
</template>
